import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useLanguage } from '../../../../../contexts/LanguageContext';

/**
 * Shared chassis-number dropdown for the VAF / PSI / CVRF warranty process forms.
 *
 * Previously each screen (VAF, PSI, CVRF) implemented its own copy of this
 * component. PSI and CVRF's copies were near-identical (client-side filtered
 * dropdown over the full chassis list); VAF's copy additionally supported
 * server-side debounced search over a paginated endpoint. Both behaviors are
 * preserved here via the `mode` prop so no screen's behavior changes.
 *
 * @param {string} mode - 'list' (default): calls `useChassisQuery()` once and
 *   filters client-side (matches previous PSI/CVRF behavior).
 *   'remote': calls `useChassisQuery({ searchText, pageSize, pageNumber })` with a
 *   debounced search term (matches previous VAF behavior).
 * @param {Function} useChassisQuery - the RTK Query hook to fetch chassis options.
 * @param {string} placeholderKey - i18n key for the placeholder text.
 */
const ChassisDropdown = ({ value, onChange, className, disabled, useChassisQuery, mode = 'list', placeholderKey }) => {
    if (mode === 'remote') {
        return (
            <RemoteSearchChassisDropdown
                value={value}
                onChange={onChange}
                className={className}
                disabled={disabled}
                useChassisQuery={useChassisQuery}
                placeholderKey={placeholderKey}
            />
        );
    }

    return (
        <ListChassisDropdown
            value={value}
            onChange={onChange}
            className={className}
            disabled={disabled}
            useChassisQuery={useChassisQuery}
            placeholderKey={placeholderKey}
        />
    );
};

// Client-side filtered dropdown over the full chassis list (previously duplicated in PSI and CVRF).
const ListChassisDropdown = ({ value, onChange, className, disabled, useChassisQuery, placeholderKey }) => {
    const { th } = useLanguage();
    const { data: chassisList = [], isLoading } = useChassisQuery(undefined, {
        refetchOnMountOrArgChange: true
    });

    const selected = chassisList.find((option) => option.chassisNo === value) || null;

    const handleSelectionChange = (e) => {
        onChange(e.value ? e.value.chassisNo : '');
    };

    return (
        <Dropdown
            value={selected}
            options={chassisList}
            onChange={handleSelectionChange}
            optionLabel="chassisNo"
            filter
            filterBy="chassisNo"
            showClear
            disabled={disabled}
            className={className}
            placeholder={th(placeholderKey)}
            loading={isLoading}
            itemTemplate={(option) => (
                <div>
                    <span style={{ fontWeight: 'bold' }} />
                    {option.chassisNo}
                </div>
            )}
        />
    );
};

// Server-side debounced search dropdown (previously VAF's ChassisDropDown.jsx).
const RemoteSearchChassisDropdown = ({ value, onChange, className, disabled, useChassisQuery, placeholderKey }) => {
    const { th } = useLanguage();
    const [selectedChassis, setSelectedChassis] = useState(null);

    // Input'ta görünen değer (controlled)
    const [filterValue, setFilterValue] = useState('');

    // API'ye giden değer (debounced)
    const [searchText, setSearchText] = useState('');

    const [initialChassisNo, setInitialChassisNo] = useState(null);

    const debounceTimerRef = useRef(null);

    const { data: chassisList = [], isLoading: isLoadingChassis } = useChassisQuery({
        searchText,
        pageSize: 50,
        pageNumber: 1
    });

    const { data: initialChassisData = [], isLoading: isLoadingInitial } = useChassisQuery(
        {
            searchText: initialChassisNo,
            pageSize: 10,
            pageNumber: 1
        },
        {
            skip: !initialChassisNo
        }
    );

    const chassisOptions = useMemo(() => {
        const allChassis = [...chassisList];

        // İnitial data varsa ve listede yoksa ekle
        if (initialChassisData.length > 0) {
            initialChassisData.forEach((initial) => {
                if (!allChassis.find((c) => c.id === initial.id)) {
                    allChassis.unshift(initial); // Başa ekle
                }
            });
        }

        return allChassis.map((chassis) => ({
            id: chassis.id,
            text: chassis.chassisNo,
            displayValue: `${chassis.id} - ${chassis.chassisNo}`
        }));
    }, [chassisList, initialChassisData]);

    const handleSelectionChange = (e) => {
        setSelectedChassis(e.value);

        // Seçim yapınca filter temizle
        setFilterValue('');
        setSearchText('');
        setInitialChassisNo(null);

        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

        // parent'a sadece şasi no
        onChange(e.value ? e.value.text : null);
    };

    // Custom filter input değişimi (debounce ile API çağrısı)
    const handleFilterInputChange = (val) => {
        setFilterValue(val);

        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

        debounceTimerRef.current = setTimeout(() => {
            setSearchText(val);
        }, 600);
    };

    // Custom filter template
    const filterTemplate = () => (
        <div className="p-dropdown-filter-container">
            <InputText
                value={filterValue}
                onChange={(e) => handleFilterInputChange(e.target.value)}
                className="p-dropdown-filter w-full"
                placeholder={th('common.search')}
                autoFocus
                type="text"
                inputMode="text"
            />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    );

    // cleanup
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    // dışarıdan value gelince selected item'ı ayarla
    useEffect(() => {
        if (!value) {
            setSelectedChassis(null);
            setInitialChassisNo(null);
            return;
        }

        const found = chassisOptions.find((o) => o.text === value);

        if (found) {
            setSelectedChassis(found);
            setInitialChassisNo(null);
        } else if (typeof value === 'string') {
            setInitialChassisNo(value);
            // Arama yapılana kadar fallback göster
            setSelectedChassis({
                id: 0,
                text: value,
                displayValue: value
            });
        }
    }, [value, chassisOptions]);

    return (
        <Dropdown
            disabled={disabled}
            value={selectedChassis}
            options={chassisOptions}
            onChange={handleSelectionChange}
            optionLabel="displayValue"
            filter
            filterTemplate={filterTemplate}
            showClear
            className={className}
            placeholder={th(placeholderKey)}
            loading={isLoadingChassis}
            emptyFilterMessage={th('common.noResultsFound')}
        />
    );
};

export default ChassisDropdown;
